// frontend/src/components/brreg/BrregExplorer.js
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios'; // Assuming axios is used in the frontend project

// Placeholder components - will be created next
// import BrregTable from './BrregTable';
// import BrregDetailsView from './BrregDetailsView';

// Assuming you have a way to get the auth token
// import { useAuth } from '../../contexts/AuthContext'; // Adjust path as needed

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5005/api';

function BrregExplorer() {
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedEntity, setSelectedEntity] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(0); // Brreg API uses 0-based paging
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);

  // const { token } = useAuth(); // Get auth token if needed

  const handleSearch = useCallback(async (page = 0) => {
    if (!searchTerm.trim()) {
      setSearchResults([]);
      setTotalPages(0);
      setTotalElements(0);
      setCurrentPage(0);
      return;
    }

    setIsLoading(true);
    setError(null);
    setSelectedEntity(null); // Clear selection on new search

    try {
      // Prepare query params for Brreg search
      // Example: Searching by name. Adapt based on desired search fields.
      const params = {
        navn: searchTerm,
        page: page,
        size: 20 // Default page size
      };

      // const config = {
      //   headers: { Authorization: `Bearer ${token}` }, // Add auth header if routes are protected
      //   params: params
      // };

      // Use GET /api/brreg/search (proxies to Brreg)
      // Adjust config if auth is needed
      const response = await axios.get(`${API_BASE_URL}/brreg/search`, { params });

      if (response.data && response.data._embedded && response.data._embedded.enheter) {
        setSearchResults(response.data._embedded.enheter);
        setTotalPages(response.data.page?.totalPages || 0);
        setTotalElements(response.data.page?.totalElements || 0);
        setCurrentPage(response.data.page?.number || 0);
      } else {
        setSearchResults([]);
        setTotalPages(0);
        setTotalElements(0);
        setCurrentPage(0);
      }
    } catch (err) {
      console.error("Error searching Brreg entities:", err);
      setError(err.response?.data?.message || 'Failed to search Brreg entities.');
      setSearchResults([]);
      setTotalPages(0);
      setTotalElements(0);
      setCurrentPage(0);
    } finally {
      setIsLoading(false);
    }
  }, [searchTerm /*, token */]); // Add token if used

  const handleSelectEntity = async (orgnr) => {
    setIsLoading(true);
    setError(null);
    try {
      // const config = {
      //   headers: { Authorization: `Bearer ${token}` } // Add auth header if routes are protected
      // };
      // Use GET /api/brreg/:orgnr
      // Adjust config if auth is needed
      const response = await axios.get(`${API_BASE_URL}/brreg/${orgnr}`);
      setSelectedEntity(response.data);
    } catch (err) {
      console.error(`Error fetching Brreg entity ${orgnr}:`, err);
      setError(err.response?.data?.message || `Failed to fetch details for ${orgnr}.`);
      setSelectedEntity(null);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 0 && newPage < totalPages) {
      handleSearch(newPage);
    }
  };

  // Trigger search when searchTerm changes (debounced effect could be added)
  // useEffect(() => {
  //   handleSearch(0); // Reset to first page on new search term
  // }, [handleSearch]); // Dependency on handleSearch (which depends on searchTerm)

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    handleSearch(0); // Trigger search on first page
  };


  return (
    <div className="p-4 md:p-6 bg-gray-50 min-h-screen">
      <h1 className="text-2xl font-semibold text-gray-800 mb-4">Brreg Entity Explorer</h1>

      {/* Search Bar */}
      <form onSubmit={handleSearchSubmit} className="mb-4 flex items-center space-x-2">
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search Brreg by name..."
          className="flex-grow px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
        />
        <button
          type="submit"
          disabled={isLoading || !searchTerm.trim()}
          className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
        >
          {isLoading ? 'Searching...' : 'Search'}
        </button>
      </form>

      {error && <div className="mb-4 p-3 bg-red-100 text-red-700 border border-red-300 rounded-md">{error}</div>}

      {/* Display Area */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Search Results / Table (Left/Main Panel) */}
        <div className="md:col-span-2 bg-white p-4 rounded-lg shadow">
          <h2 className="text-lg font-medium text-gray-700 mb-3">Search Results</h2>
          {isLoading && !searchResults.length && <p>Loading results...</p>}
          {!isLoading && !searchResults.length && searchTerm && <p>No results found for "{searchTerm}".</p>}
          {!isLoading && !searchResults.length && !searchTerm && <p>Enter a search term above.</p>}

          {/* Placeholder for BrregTable */}
          {searchResults.length > 0 && (
            <div>
              <p className="text-sm text-gray-600 mb-2">
                Showing page {currentPage + 1} of {totalPages} ({totalElements} total results)
              </p>
              {/* <BrregTable results={searchResults} onSelect={handleSelectEntity} /> */}
              <div className="overflow-x-auto">
                 <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                        <tr>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Org Nr</th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Action</th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {searchResults.map(entity => (
                            <tr key={entity.organisasjonsnummer}>
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{entity.navn}</td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{entity.organisasjonsnummer}</td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{entity.organisasjonsform?.beskrivelse || 'N/A'}</td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                    <button
                                        onClick={() => handleSelectEntity(entity.organisasjonsnummer)}
                                        className="text-indigo-600 hover:text-indigo-900"
                                    >
                                        View Details
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
              </div>
              {/* Pagination Controls */}
              {totalPages > 1 && (
                <div className="mt-4 flex justify-between items-center">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 0 || isLoading}
                    className="px-3 py-1 border border-gray-300 rounded-md text-sm hover:bg-gray-50 disabled:opacity-50"
                  >
                    Previous
                  </button>
                  <span className="text-sm text-gray-700">
                    Page {currentPage + 1} of {totalPages}
                  </span>
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage >= totalPages - 1 || isLoading}
                    className="px-3 py-1 border border-gray-300 rounded-md text-sm hover:bg-gray-50 disabled:opacity-50"
                  >
                    Next
                  </button>
                </div>
              )}
            </div>
          )}
           {/* End Placeholder */}
        </div>

        {/* Details View (Right Panel) */}
        <div className="md:col-span-1 bg-white p-4 rounded-lg shadow">
           <h2 className="text-lg font-medium text-gray-700 mb-3">Entity Details</h2>
           {isLoading && selectedEntity && <p>Loading details...</p>}
           {!selectedEntity && <p className="text-sm text-gray-500">Select an entity from the search results to view details.</p>}

           {/* Placeholder for BrregDetailsView */}
           {selectedEntity && (
             <div>
                <h3 className="text-md font-semibold text-gray-800">{selectedEntity.navn}</h3>
                <p className="text-sm text-gray-600 mb-2">Org Nr: {selectedEntity.organisasjonsnummer}</p>
                {/* Display more fields from selectedEntity */}
                <pre className="mt-2 p-2 bg-gray-100 rounded text-xs overflow-auto">
                    {JSON.stringify(selectedEntity, null, 2)}
                </pre>
             </div>
           )}
           {/* End Placeholder */}
        </div>
      </div>
    </div>
  );
}

export default BrregExplorer;
